import React, { useState } from 'react';
import type { SecurityDepositEnrollmentViewProps } from 'interfaces/enrollments/SecurityDepositEnrollmentViewProps';
import { PaymentStatus } from 'models/payments/PaymentStatus';
import { ApiService } from 'services/ApiService';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatCurrency = (minorUnits: number, currency: string): string =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(minorUnits / 100);

const STATUS_LABEL: Record<PaymentStatus, string> = {
  [PaymentStatus.Pending]: 'Awaiting Payment',
  [PaymentStatus.RequiresAction]: 'Action Required',
  [PaymentStatus.Succeeded]: 'Paid',
  [PaymentStatus.Failed]: 'Payment Failed',
  [PaymentStatus.Refunded]: 'Refunded',
  [PaymentStatus.PartiallyRefunded]: 'Partially Refunded',
  [PaymentStatus.Canceled]: 'Canceled',
};

const STATUS_BADGE: Record<PaymentStatus, string> = {
  [PaymentStatus.Pending]: 'bg-yellow-100 text-yellow-700 border border-yellow-300',
  [PaymentStatus.RequiresAction]: 'bg-orange-100 text-orange-700 border border-orange-300',
  [PaymentStatus.Succeeded]: 'bg-green-100 text-green-700 border border-green-300',
  [PaymentStatus.Failed]: 'bg-red-100 text-red-700 border border-red-300',
  [PaymentStatus.Refunded]: 'bg-gray-100 text-gray-600 border border-gray-300',
  [PaymentStatus.PartiallyRefunded]: 'bg-gray-100 text-gray-600 border border-gray-300',
  [PaymentStatus.Canceled]: 'bg-gray-100 text-gray-500 border border-gray-300',
};

const isActionable = (status: PaymentStatus): boolean =>
  status === PaymentStatus.Pending || status === PaymentStatus.RequiresAction;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const SecurityDepositEnrollmentView: React.FC<SecurityDepositEnrollmentViewProps> = ({
  enrollment,
  isDisabled = false,
  onStatusRefresh,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const status = enrollment.localPaymentStatus;
  const awaitingTeam = enrollment.isAwaitingTeamInput;
  const formattedAmount = formatCurrency(enrollment.amountMinorUnits, enrollment.currency);

  const handlePayDeposit = async () => {
    setLoading(true);
    setError(null);
    try {
      const { checkoutUrl } = await ApiService.createDepositCheckoutSession(enrollment.id);
      // Same-tab navigation so Stripe's success/cancel redirect lands back in this app
      // (see DepositReturnPage) instead of leaving the customer stranded in a new tab.
      window.location.assign(checkoutUrl);
    } catch {
      setError('Unable to start the payment process. Please try again or contact support.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 bg-white border border-gray-200 rounded-lg shadow-sm space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <h2 className="text-lg font-semibold text-gray-800">Security Deposit</h2>
        {!awaitingTeam && (
          <span
            className={`inline-flex items-center rounded-full px-3 py-0.5 text-xs font-semibold ${STATUS_BADGE[status]}`}
            aria-label={`Payment status: ${STATUS_LABEL[status]}`}
          >
            {STATUS_LABEL[status]}
          </span>
        )}
        {awaitingTeam && (
          <span
            className="inline-flex items-center rounded-full px-3 py-0.5 text-xs font-semibold bg-blue-100 text-blue-700 border border-blue-300"
            aria-label="Payment status: Being prepared"
          >
            Being Prepared
          </span>
        )}
      </div>

      {/* Amount — hidden until the team finalizes the per-client details */}
      {!awaitingTeam && (
        <div className="flex items-baseline gap-2">
          <span className="text-3xl font-bold text-gray-900">{formattedAmount}</span>
          <span className="text-sm text-gray-500">{enrollment.currency.toUpperCase()}</span>
        </div>
      )}

      {/* Description */}
      {!awaitingTeam && enrollment.description && (
        <p className="text-sm text-gray-600">{enrollment.description}</p>
      )}

      {/* Status-specific messaging */}
      {status === PaymentStatus.Succeeded && (
        <div className="flex items-center gap-2 rounded-lg bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-700">
          <svg className="h-4 w-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <span>Your deposit has been received. Thank you!</span>
        </div>
      )}

      {status === PaymentStatus.Failed && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 space-y-1">
          <p className="font-medium">Payment failed</p>
          <p>Your payment could not be processed. Please try again or use a different payment method.</p>
        </div>
      )}

      {status === PaymentStatus.RequiresAction && (
        <div className="rounded-lg bg-orange-50 border border-orange-200 px-4 py-3 text-sm text-orange-700">
          <p>Additional verification is required to complete your payment. Click "Pay Deposit" below to continue.</p>
        </div>
      )}

      {status === PaymentStatus.Canceled && (
        <div className="rounded-lg bg-gray-50 border border-gray-200 px-4 py-3 text-sm text-gray-600">
          <p>This deposit has been canceled. Please contact your artist if you think this is a mistake.</p>
        </div>
      )}

      {(status === PaymentStatus.Refunded || status === PaymentStatus.PartiallyRefunded) && (
        <div className="rounded-lg bg-gray-50 border border-gray-200 px-4 py-3 text-sm text-gray-600">
          <p>{status === PaymentStatus.Refunded ? 'This deposit has been fully refunded.' : 'This deposit has been partially refunded.'}</p>
        </div>
      )}
                                    
      {enrollment.isAwaitingTeamInput && status === PaymentStatus.Pending && (
        <div className="flex items-start gap-2 rounded-lg bg-blue-50 border border-blue-200 px-4 py-3 text-sm text-blue-700">
          <svg className="h-4 w-4 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>Your artist is setting up the deposit. Check back shortly or refresh your status below.</span>
        </div>
      )}

      {/* Error */}
      {error && (
        <p className="text-sm text-red-600" role="alert">{error}</p>
      )}

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-3 pt-1">
        {isActionable(status) && !enrollment.isAwaitingTeamInput && (
          <button
            type="button"
            onClick={handlePayDeposit}
            disabled={isDisabled || loading}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading && (
              <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
            )}
            {loading ? 'Redirecting…' : 'Pay Deposit'}
          </button>
        )}
        {onStatusRefresh && (
          <button
            type="button"
            onClick={onStatusRefresh}
            className="text-sm text-gray-500 hover:text-gray-700 underline focus:outline-none focus:ring-2 focus:ring-gray-400 rounded"
          >
            Refresh status
          </button>
        )}
      </div>
    </div>
  );
};

export default SecurityDepositEnrollmentView;
